import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
} from "react";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { app } from "../firebase";
import {
  IUser,
  IUserPermissions,
  PermissionAction,
  PermissionResource,
} from "../interfaces";
import { api } from "../api";

interface IAuthState {
  isAuthenticated: boolean;
  user: IUser | null;
  isLoading: boolean;
  idToken: string | null;
  error: string | null;
  permissions: IUserPermissions | null;
  isAdmin: boolean;
  can: (action: PermissionAction, resource: PermissionResource) => boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const initialAuthState: IAuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
  error: null,
  idToken: null,
  permissions: null,
  isAdmin: false,
  can: () => false,
  loginWithGoogle: async () => {},
  logout: async () => {},
  getToken: async () => null,
};

const AuthContext = createContext<IAuthState>(initialAuthState);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);
  const [user, setUser] = React.useState<IUser | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const auth = getAuth(app);
  const [idToken, setIdToken] = React.useState<string | null>(null);
  const [firebaseUser, setFirebaseUser] = React.useState<any>(null);
  const [permissions, setPermissions] = React.useState<IUserPermissions | null>(
    null
  );

  const fetchUserData = async (idToken: string): Promise<IUser | null> => {
    if (!idToken) return null;

    try {
      const response = await api.get<IUser>("/user", {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      return response;
    } catch (error: any) {
      setError(error?.message || "Failed to fetch user data");
    }

    return null;
  };

  const fetchPermissions = async (
    idToken: string
  ): Promise<IUserPermissions | null> => {
    if (!idToken) return null;
    try {
      return await api.get<IUserPermissions>("/user/permissions", {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
    } catch (error: any) {
      setError(error?.message || "Failed to fetch permissions");
      return null;
    }
  };

  const getToken = async (): Promise<string | null> => {
    if (!firebaseUser) return null;
    try {
      return await firebaseUser.getIdToken();
    } catch (error) {
      setError("Failed to refresh token");
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setFirebaseUser(user);
        const idToken = await user.getIdToken();
        setIdToken(idToken);

        const [userData, userPermissions] = await Promise.all([
          fetchUserData(idToken),
          fetchPermissions(idToken),
        ]);

        setIsAuthenticated(true);
        setUser(userData);
        setPermissions(userPermissions);
        setIsLoading(false);
        setError(null);
      } else {
        setFirebaseUser(null);
        setIsAuthenticated(false);
        setUser(null);
        setPermissions(null);
        setIsLoading(false);
        setError(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      // Auth state change listener will update the state
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || "Failed to login with Google");
      throw error;
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await signOut(auth);
      // Auth state change listener will update the state
    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || "Failed to logout");
      throw error;
    }
  };

  const isAdmin = permissions?.isAdmin ?? false;

  const can = useCallback(
    (action: PermissionAction, resource: PermissionResource): boolean => {
      if (!permissions) return false;
      if (permissions.isAdmin) return true;
      return permissions.permissions.some(
        (p) => p.action === action && p.resource === resource
      );
    },
    [permissions]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        isLoading,
        error,
        idToken,
        permissions,
        isAdmin,
        can,
        loginWithGoogle,
        logout,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
